// CSRF (Cross-Site Request Forgery) Protection
// Implements security recommendations from July 2025 audit

import { webCrypto } from '@/lib/crypto/web-crypto'

export class CSRF {
  private static readonly TOKEN_LENGTH = 32
  private static readonly TOKEN_KEY = 'qkey-csrf-token'
  private static readonly HEADER_NAME = 'X-CSRF-Token'
  
  /**
   * Generate a new CSRF token
   */
  static generateToken(): string {
    const bytes = webCrypto.generateRandomBytes(this.TOKEN_LENGTH)
    return webCrypto.bytesToHex(bytes)
  }
  
  /**
   * Store CSRF token in sessionStorage
   */
  static storeToken(token: string): void {
    if (typeof window !== 'undefined') {
      sessionStorage.setItem(this.TOKEN_KEY, token)
    }
  }
  
  /**
   * Get stored CSRF token
   */
  static getToken(): string | null {
    if (typeof window !== 'undefined') {
      return sessionStorage.getItem(this.TOKEN_KEY)
    }
    return null
  }
  
  /**
   * Clear CSRF token
   */
  static clearToken(): void {
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem(this.TOKEN_KEY)
    }
  }
  
  /**
   * Add CSRF token to request headers
   */
  static addToHeaders(headers: HeadersInit = {}): HeadersInit {
    const token = this.getToken()
    if (token) {
      return {
        ...headers,
        [this.HEADER_NAME]: token
      }
    }
    return headers
  }
  
  /**
   * Validate CSRF token from request
   */
  static validateToken(requestToken: string | null): boolean {
    if (!requestToken) return false
    const storedToken = this.getToken()
    return storedToken === requestToken
  }
  
  /**
   * Create a new token and store it
   */
  static initializeToken(): string {
    const token = this.generateToken()
    this.storeToken(token)
    return token
  }
  
  /**
   * Middleware for fetch requests
   */
  static async fetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    const method = init?.method?.toUpperCase() || 'GET'
    
    // Only add CSRF token for state-changing methods
    if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(method)) {
      init = {
        ...init,
        headers: this.addToHeaders(init?.headers)
      }
    }
    
    return fetch(input, init)
  }
}

// Cookie configuration for SameSite protection
export const cookieConfig = {
  secure: true, // HTTPS only
  sameSite: 'strict' as const, // Prevent CSRF via cookies
  httpOnly: true, // Prevent XSS access
  path: '/',
  maxAge: 86400 // 24 hours
}

// Helper to set secure cookies
export function setSecureCookie(name: string, value: string, options = cookieConfig): void {
  if (typeof document === 'undefined') return
  
  const parts = [`${name}=${encodeURIComponent(value)}`]
  
  if (options.secure) parts.push('Secure')
  if (options.httpOnly) parts.push('HttpOnly')
  if (options.sameSite) parts.push(`SameSite=${options.sameSite}`)
  if (options.path) parts.push(`Path=${options.path}`)
  if (options.maxAge) parts.push(`Max-Age=${options.maxAge}`)
  
  document.cookie = parts.join('; ')
}